import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { createUser } from '../api/client';

interface CreateUserFormProps {
  onUserCreated?: () => void;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onUserCreated }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    isAdmin: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'isAdmin' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error and success when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const validateForm = (): string | null => {
    if (!formData.email) return 'Email is required';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters long';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';
    
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await createUser(formData.email, formData.password, formData.isAdmin);
      setSuccess(result.message);
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        isAdmin: false
      });
      
      // Notify parent component
      if (onUserCreated) {
        onUserCreated();
      }
    } catch (err: any) {
      // Enhanced error handling to show more details
      let errorMessage = 'Failed to create user';
      
      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const data = err.response.data;
        
        if (data && data.detail) {
          errorMessage = data.detail;
        } else if (status === 400) {
          errorMessage = 'Bad request - please check your input';
        } else if (status === 401) {
          errorMessage = 'Unauthorized - please check your admin privileges';
        } else if (status === 403) {
          errorMessage = 'Forbidden - you do not have permission to create users';
        } else if (status === 500) {
          errorMessage = 'Server error - please try again later';
        } else {
          errorMessage = `Server error (${status})`;
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server - please check your connection';
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      console.error('Detailed error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      isAdmin: false
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          Create New User
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new user account with email and password. You can assign admin privileges during creation.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            disabled={loading}
            fullWidth
            required
            placeholder="user@example.com"
          />

          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            disabled={loading}
            fullWidth
            required
            helperText="Minimum 6 characters"
          />

          <TextField
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            disabled={loading}
            fullWidth
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isAdmin}
                onChange={handleInputChange('isAdmin')}
                disabled={loading}
              />
            }
            label="Grant Admin Privileges"
          />

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
            >
              {loading ? 'Creating User...' : 'Create User'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              onClick={handleClear}
              disabled={loading}
              sx={{ minWidth: '120px' }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default CreateUserForm; 